/**
 * OneTrust cookie-group ids: C0002 is analytics/performance cookies, C0003 is
 * functional cookies, and C0004 is advertising/targeting cookies. OneTrust
 * fires `OneTrustGroupsUpdated` on `window` on every consent change (initial
 * load, banner choice, preference center edits), which is what the
 * consent-gated helpers below listen for.
 */
const ANALYTICS_CONSENT_GROUP = "C0002"
const FUNCTIONAL_CONSENT_GROUP = "C0003"
const ADVERTISING_CONSENT_GROUP = "C0004"

let consentSyncInitialized = false

export function initializeConsentSync() {
  if (consentSyncInitialized) return

  consentSyncInitialized = true
  syncGoogleConsentFromOneTrust()

  const oneTrust = window.OneTrust
  if (oneTrust?.OnConsentChanged) {
    oneTrust.OnConsentChanged(syncGoogleConsentFromOneTrust)
  }

  window.addEventListener(
    "OneTrustGroupsUpdated",
    syncGoogleConsentFromOneTrust,
  )
  document.addEventListener("DOMContentLoaded", () =>
    setTimeout(syncGoogleConsentFromOneTrust, 0),
  )
}

/**
 * Runs `callback` once advertising consent (C0004) is granted — immediately
 * if it already is, otherwise on the first OneTrust update that grants it.
 * Gate for anything that identifies or targets the visitor: Conversions API
 * relays, hashed user fields, ad click-id cookies.
 */
export function runAfterAdvertisingConsent(callback: () => void) {
  runWhen(hasAdvertisingConsent, callback)
}

/**
 * Runs `callback` once analytics consent (C0002) is granted — immediately
 * if it already is, otherwise on the first OneTrust update that grants it.
 */
export function runAfterAnalyticsConsent(callback: () => void) {
  runWhen(hasAnalyticsConsent, callback)
}

/**
 * Runs `callback` once analytics (C0002) or advertising (C0004) consent is
 * granted. Gate for `storyworth.pixel` data-layer pushes: GTM then applies
 * each tag's own consent requirement, so an analytics-only visitor gets GA4
 * ecommerce events while ad-vendor tags stay silent.
 */
export function runAfterMeasurementConsent(callback: () => void) {
  runWhen(hasMeasurementConsent, callback)
}

/**
 * Runs `callback` once the OneTrust group `group` (e.g. "C0003") is granted,
 * for vendors whose consent category is configured rather than fixed.
 */
export function runAfterConsentGroup(group: string, callback: () => void) {
  runWhen(() => activeConsentGroups().includes(group), callback)
}

function runWhen(condition: () => boolean, callback: () => void) {
  if (condition()) {
    callback()
    return
  }

  const listener = () => {
    if (!condition()) return

    window.removeEventListener("OneTrustGroupsUpdated", listener)
    callback()
  }
  window.addEventListener("OneTrustGroupsUpdated", listener)
}

export function hasAdvertisingConsent() {
  return activeConsentGroups().includes(ADVERTISING_CONSENT_GROUP)
}

export function hasAnalyticsConsent() {
  return activeConsentGroups().includes(ANALYTICS_CONSENT_GROUP)
}

export function hasFunctionalConsent() {
  return activeConsentGroups().includes(FUNCTIONAL_CONSENT_GROUP)
}

export function hasMeasurementConsent() {
  return hasAnalyticsConsent() || hasAdvertisingConsent()
}

export function activeConsentGroups() {
  const groups = window.OnetrustActiveGroups || window.OptanonActiveGroups || ""
  return groups
    .split(",")
    .map((group) => group.trim())
    .filter(Boolean)
}

export function resetConsentForTests() {
  consentSyncInitialized = false
}

/**
 * True once OneTrust has loaded and published the active consent groups.
 * Until then we don't know the visitor's choice, so Google Consent Mode is
 * left on its defaults (with `wait_for_update`) rather than told "denied".
 */
export function oneTrustHasReported() {
  return (
    window.OnetrustActiveGroups !== undefined ||
    window.OptanonActiveGroups !== undefined
  )
}

function syncGoogleConsentFromOneTrust() {
  if (!oneTrustHasReported()) return

  window.dataLayer = window.dataLayer || []
  window.gtag =
    window.gtag ||
    ((...args) => {
      window.dataLayer?.push(args)
    })

  const analyticsAllowed = hasAnalyticsConsent()
  const advertisingAllowed = hasAdvertisingConsent()

  window.gtag("consent", "update", {
    analytics_storage: analyticsAllowed ? "granted" : "denied",
    ad_storage: advertisingAllowed ? "granted" : "denied",
    ad_user_data: advertisingAllowed ? "granted" : "denied",
    ad_personalization: advertisingAllowed ? "granted" : "denied",
  })
}
